/**
 * Zone Lap Data
 *
 * Calculates per-lap detailed data for each zone, used for
 * lap-by-lap breakdowns and outlier detection.
 */

import { type ZoneLapData, type ZoneConsensusMetrics } from './TrackZoneTypes'
import { type ZoneDataPoint } from './ZoneDetection'
import { G_CONVERSION } from './LapZoneMetrics'
import {
  LAP_TABLE_BRAKING_G,
  BRAKE_RELEASE_G,
  BRAKE_RELEASE_RELIABILITY_MULTIPLE,
  THROTTLE_G,
} from '../config/zoneDetection'
import type { RecalibratedZoneLapMetrics } from '../sessionAnalysis/zoneDisplay/recalibratedZoneMetrics'
import {
  findBrakePhaseFromPoints,
  type BrakePhaseSample,
} from './brakePhasePoints'
import { KMH_PER_MPS } from '../../utils/speedUnits'
import { MS_PER_S } from '../../utils/timeUnits'

// ============================================================================
// Per-Lap Zone Data
// ============================================================================

/**
 * Whether a lap's brake-release point is reliable enough to render as a
 * map marker. The release point is the BRAKE_RELEASE_G crossing before the
 * apex on the chart's per-point acceleration signal (see brakePhasePoints;
 * laps without per-point acceleration fall back to the bucket signal). On a
 * lap whose peak braking barely exceeds that threshold, the crossing sits in
 * the noise band just past the peak and its position is arbitrary. Marker
 * builders (mobile useZoneMarkers, web zoneRacingLines) gate on this;
 * consistency/variance consumers do not.
 */
export function hasReliableBrakeRelease(
  lap: Pick<ZoneLapData, 'peakBrakingG'>
): boolean {
  return (
    lap.peakBrakingG >=
    Math.abs(BRAKE_RELEASE_G) * BRAKE_RELEASE_RELIABILITY_MULTIPLE
  )
}

/** Calculate per-lap data for a zone */
export function calculateZoneLapData(
  zoneId: string,
  zoneData: ZoneDataPoint[][],
  lapIds: string[],
  lapNumbers: number[],
  consensus: ZoneConsensusMetrics,
  zoneStartDistance: number,
  sessionIds?: string[],
  /**
   * Per-lap zone metrics measured on the recalibrated frame (pass 25 time,
   * pass 26 speeds + apex) — complete for every lapId or absent (per-zone
   * atomicity, enforced by computeRecalibratedZoneMetrics). When absent the
   * odometer-frame values apply, which are only comparable WITHIN a lap,
   * never across laps (see zoneTimeFrameDrift.charact.test.ts).
   */
  recalibratedMetrics?: Map<string, RecalibratedZoneLapMetrics>,
  /**
   * Per-lap slices of the chart's own per-GPS-point acceleration series,
   * positionally aligned with `zoneData` (one entry per lap; an entry may be
   * undefined when the lap carries no per-point acceleration). When present
   * and usable it supplies brake point / brake release / peak braking g —
   * the marker then sits on the trace the rider reads. Otherwise the
   * bucket-derived values apply, unchanged. See brakePhasePoints.
   */
  pointSeries?: (BrakePhaseSample[] | undefined)[]
): ZoneLapData[] {
  // First pass: find each lap's raw apex index to compute the median
  const medianApexIdx = computeMedianApexIndex(zoneData)

  return zoneData.map((data, idx) => {
    const lapId = lapIds[idx] ?? `lap-${idx}`
    const lapNumber = lapNumbers[idx] ?? idx + 1
    const sessionId = sessionIds?.[idx]

    if (data.length === 0) {
      return createEmptyLapData(lapId, lapNumber, sessionId)
    }

    return extractLapData(
      data,
      lapId,
      lapNumber,
      consensus,
      zoneStartDistance,
      sessionId,
      medianApexIdx,
      recalibratedMetrics?.get(lapId),
      pointSeries?.[idx]
    )
  })
}

function createEmptyLapData(
  lapId: string,
  lapNumber: number,
  sessionId?: string
): ZoneLapData {
  return {
    lapId,
    lapNumber,
    sessionId,
    entrySpeed: 0,
    minSpeed: 0,
    exitSpeed: 0,
    entryDistance: 0,
    exitDistance: 0,
    peakLeanAngle: 0,
    zoneTimeMs: 0,
    isOutlier: false,
    deviationScore: 0,
    brakingPointDistance: null,
    brakeReleaseDistance: null,
    peakBrakingG: 0,
    apexDistance: 0,
    apexLat: 0,
    apexLng: 0,
    throttlePointDistance: null,
    peakThrottleG: 0,
    coastingDistance: 0,
  }
}

function extractLapData(
  data: ZoneDataPoint[],
  lapId: string,
  lapNumber: number,
  consensus: ZoneConsensusMetrics,
  zoneStartDistance: number,
  sessionId?: string,
  medianApexIdx?: number,
  recalibrated?: RecalibratedZoneLapMetrics,
  pointSamples?: BrakePhaseSample[]
): ZoneLapData {
  const speeds = extractSpeeds(data)
  const distances = extractDistances(data)
  const peakLeanAngle = Math.max(...data.map((p) => Math.abs(p.leanAngle)))
  const zoneTimeMs = recalibrated?.timeMs ?? calculateZoneTime(data)

  const apex = findApex(data, zoneStartDistance, medianApexIdx)
  // The rider-visible brake markers belong on the chart's per-point trace;
  // the bucket signal is the fallback for laps without one (brakePhasePoints).
  const braking =
    (pointSamples
      ? findBrakePhaseFromPoints(pointSamples, zoneStartDistance)
      : null) ?? findBrakingMetrics(data, zoneStartDistance, apex.apexIdx)
  const throttle = findThrottleMetrics(data, zoneStartDistance, apex.apexIdx)
  const coastingDistance = calculateCoasting(
    data,
    zoneStartDistance,
    braking.brakeReleaseDistance,
    throttle.throttlePointDistance
  )

  // Use apex speed (from findApex) as minSpeed to guarantee the table
  // value matches the marker position on the map.
  const apexSpeed = apex.minSpeed
  // deviationScore stays on the odometer frame — consensus.avgMinSpeed is
  // odometer-derived
  const deviationScore = calculateDeviationScore(
    apexSpeed,
    peakLeanAngle,
    consensus
  )

  return {
    lapId,
    lapNumber,
    sessionId,
    entrySpeed: recalibrated?.entrySpeed ?? speeds.entrySpeed,
    minSpeed: recalibrated?.minSpeed ?? apexSpeed,
    exitSpeed: recalibrated?.exitSpeed ?? speeds.exitSpeed,
    entryDistance: distances.entryDistance,
    exitDistance: distances.exitDistance,
    peakLeanAngle,
    zoneTimeMs,
    isOutlier: deviationScore > 0.3,
    deviationScore,
    brakingPointDistance: braking.brakingPointDistance,
    brakeReleaseDistance: braking.brakeReleaseDistance,
    peakBrakingG: braking.peakBrakingG,
    apexDistance: apex.apexDistance,
    apexLat: apex.apexLat,
    apexLng: apex.apexLng,
    throttlePointDistance: throttle.throttlePointDistance,
    peakThrottleG: throttle.peakThrottleG,
    coastingDistance,
    apexRefDistance: recalibrated?.apexDistance,
  }
}

function extractSpeeds(data: ZoneDataPoint[]) {
  const entrySpeed = data[0].speed
  const exitSpeed = data[data.length - 1].speed
  const minSpeed = Math.min(...data.map((p) => p.speed))
  return { entrySpeed, exitSpeed, minSpeed }
}

function extractDistances(data: ZoneDataPoint[]) {
  const entryDistance = data[0].distance
  const exitDistance = data[data.length - 1].distance
  const zoneLength = exitDistance - entryDistance
  return { entryDistance, exitDistance, zoneLength }
}

/**
 * Calculate zone time by integrating actual speed across data points.
 * Uses trapezoidal rule: for each pair of consecutive points,
 * time = distance / average_speed.
 *
 * This is much more accurate than averaging entry+exit speed, which
 * ignores the speed profile through the zone (e.g., slowing to apex
 * then accelerating out of a corner).
 */
function calculateZoneTime(data: ZoneDataPoint[]): number {
  if (data.length < 2) return 0

  let totalTimeMs = 0
  for (let i = 0; i < data.length - 1; i++) {
    const dx = data[i + 1].distance - data[i].distance
    if (dx <= 0) continue

    const avgSpeedKmh = (data[i].speed + data[i + 1].speed) / 2
    if (avgSpeedKmh <= 0) continue

    const avgSpeedMs = avgSpeedKmh / KMH_PER_MPS
    totalTimeMs += (dx / avgSpeedMs) * MS_PER_S
  }

  return Math.round(totalTimeMs)
}

/**
 * Find the apex (slowest point) by searching the full zone.
 *
 * The zone boundaries already limit the search area, so no additional
 * constraint is needed. This ensures the apex marker and minSpeed value
 * always refer to the same point — the actual slowest point in the zone.
 */
function findApex(
  data: ZoneDataPoint[],
  zoneStartDistance: number,
  _medianApexIdx?: number
) {
  let apexDistance = 0
  let minSpeed = Infinity
  let apexIdx = 0
  let apexLat = 0
  let apexLng = 0

  for (let i = 0; i < data.length; i++) {
    if (data[i].speed < minSpeed) {
      minSpeed = data[i].speed
      apexDistance = data[i].distance - zoneStartDistance
      apexIdx = i
      apexLat = data[i].lat
      apexLng = data[i].lng
    }
  }

  return { apexDistance, apexIdx, minSpeed, apexLat, apexLng }
}

/** Compute the median apex index across all laps (first pass). */
function computeMedianApexIndex(
  zoneData: ZoneDataPoint[][]
): number | undefined {
  const apexIndices: number[] = []

  for (const lapData of zoneData) {
    if (lapData.length === 0) continue
    let minSpeed = Infinity
    let apexIdx = 0
    for (let i = 0; i < lapData.length; i++) {
      if (lapData[i].speed < minSpeed) {
        minSpeed = lapData[i].speed
        apexIdx = i
      }
    }
    apexIndices.push(apexIdx)
  }

  if (apexIndices.length === 0) return undefined

  apexIndices.sort((a, b) => a - b)
  const mid = Math.floor(apexIndices.length / 2)
  return apexIndices.length % 2 === 1
    ? apexIndices[mid]
    : Math.round((apexIndices[mid - 1] + apexIndices[mid]) / 2)
}

function findBrakingMetrics(
  data: ZoneDataPoint[],
  zoneStartDistance: number,
  apexIdx: number
) {
  let brakingPointDistance: number | null = null
  let brakeReleaseDistance: number | null = null
  let peakBrakingG = 0
  let foundBraking = false
  let maxDecelMs2 = 0
  let lastHeavyBrakingIdx = -1

  for (let i = 0; i < data.length; i++) {
    const point = data[i]
    const accelG = point.acceleration / G_CONVERSION

    // Find where braking starts (first point below threshold)
    if (!foundBraking && accelG < LAP_TABLE_BRAKING_G) {
      brakingPointDistance = point.distance - zoneStartDistance
      foundBraking = true
    }

    // Acceleration is pre-smoothed at the lap level by analyzeTrackZones
    if (data[i].acceleration < maxDecelMs2) {
      maxDecelMs2 = data[i].acceleration
      peakBrakingG = Math.abs(data[i].acceleration / G_CONVERSION)
    }

    // Track the last point of heavy braking (before apex)
    // This is where the rider transitions from active braking to trail braking
    if (i <= apexIdx && accelG < BRAKE_RELEASE_G) {
      lastHeavyBrakingIdx = i
    }
  }

  // Brake release is where heavy braking ends (last point with significant deceleration)
  if (lastHeavyBrakingIdx >= 0) {
    brakeReleaseDistance =
      data[lastHeavyBrakingIdx].distance - zoneStartDistance
  }

  return { brakingPointDistance, brakeReleaseDistance, peakBrakingG }
}

function findThrottleMetrics(
  data: ZoneDataPoint[],
  zoneStartDistance: number,
  apexIdx: number
) {
  let throttlePointDistance: number | null = null
  let peakThrottleG = 0
  let foundThrottle = false
  let maxAccelMs2 = 0

  for (let i = apexIdx >= 0 ? apexIdx : 0; i < data.length; i++) {
    const point = data[i]
    const accelG = point.acceleration / G_CONVERSION
    if (!foundThrottle && accelG > THROTTLE_G) {
      throttlePointDistance = point.distance - zoneStartDistance
      foundThrottle = true
    }
    // Acceleration is pre-smoothed at the lap level by analyzeTrackZones
    if (data[i].acceleration > maxAccelMs2) {
      maxAccelMs2 = data[i].acceleration
      peakThrottleG = data[i].acceleration / G_CONVERSION
    }
  }

  return { throttlePointDistance, peakThrottleG }
}

function calculateCoasting(
  _data: ZoneDataPoint[],
  _zoneStartDistance: number,
  brakeReleaseDistance: number | null,
  throttlePointDistance: number | null
): number {
  // Coasting is the distance between brake release and throttle pickup
  if (brakeReleaseDistance === null || throttlePointDistance === null) {
    return 0
  }

  return Math.max(0, throttlePointDistance - brakeReleaseDistance)
}

function calculateDeviationScore(
  minSpeed: number,
  peakLeanAngle: number,
  consensus: ZoneConsensusMetrics
): number {
  const speedDev =
    Math.abs(minSpeed - consensus.avgMinSpeed) / (consensus.avgMinSpeed || 1)
  const leanDev =
    Math.abs(peakLeanAngle - consensus.peakLeanAngle) /
    (consensus.peakLeanAngle || 1)
  return (speedDev + leanDev) / 2
}
